import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';

const ShopifyLanding = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Check if this is an embedded app request
        const embedded = searchParams.get('embedded');
        const hmac = searchParams.get('hmac');
        const shop = searchParams.get('shop');

        if (embedded === '1' && hmac && shop) {
            validateEmbeddedRequest(shop, hmac);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const validateEmbeddedRequest = async (shop, hmac) => {
        try {
            setIsValidating(true);
            setError('');

            // Prepare all query parameters for HMAC validation
            const queryParams = {};
            searchParams.forEach((value, key) => {
                queryParams[key] = value;
            });

            console.log('Sending query params for HMAC validation:', queryParams);

            // Call the authorization endpoint with shop_domain, hmac, and all query_params
            const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/authorize/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    shop_domain: shop,
                    hmac: hmac,
                    query_params: queryParams
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                // Store auth token and related data using utility
                const { ShopifyAuth } = await import('../utils/shopifyAuth');
                ShopifyAuth.setAuthData(result.auth_token, shop, searchParams.get('host'));

                // Extract shop name from domain for URL
                const shopName = shop.replace('.myshopify.com', '');

                // Redirect to shop-specific dashboard
                navigate(`/shopify/${shopName}/dashboard`, { replace: true });
            } else {
                throw new Error(result.error || 'Authorization failed');
            }

        } catch (err) {
            console.error('Embedded app validation error:', err);
            setError(`Authorization failed: ${err.message}`);
        } finally {
            setIsValidating(false);
        }
    };

    // Show validation screen for embedded requests
    if (isValidating) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="success" style={{ width: '3rem', height: '3rem' }} />
                    <h4 className="mt-3 text-success">Validating Shopify App</h4>
                    <p className="text-muted">Authenticating your store...</p>
                </div>
            </div>
        );
    }

    // Show error screen if validation failed
    if (error) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6}>
                            <Alert variant="danger">
                                <Alert.Heading>Authentication Error</Alert.Heading>
                                <p>{error}</p>
                                <hr />
                                <div className="d-flex justify-content-end">
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => {
                                            setError('');
                                            navigate('/shopify/app');
                                        }}
                                    >
                                        Try Manual Installation
                                    </Button>
                                </div>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Regular landing page for non-embedded requests
    return (
        <div className="shopify-landing-page" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={10}>
                        {/* Header Section */}
                        <div className="text-center mb-3">
                            <h1 className="display-2 fw-bold text-orange mb-3">
                                Shopify Integration
                            </h1>
                            <h4 className="display-5 fw-bold text-dark mb-4">
                                ALANI360 - Mercado Libre
                            </h4>
                            <p className="lead text-muted">
                                Seamlessly connect your Shopify store with ALANI360's powerful e-commerce ecosystem
                            </p>
                            <img width={180} src="/images/logo_large_plus@2x.webp" alt="MercadoLibre" className="img-fluid" />
                            <img width={100} src="/images/by_alani.jpg" alt="by ALANI360" className="img-fluid" />
                        </div>

                        {/* Features Section */}
                        <Row className="g-4 mb-5">
                            <Col md={4}>
                                <Card className="h-100 shadow-sm">
                                    <Card.Body className="text-center">
                                        <div className="mb-3">
                                            <i className="fas fa-sync-alt fa-3x text-success"></i>
                                        </div>
                                        <h5>Real-time Sync</h5>
                                        <p className="text-muted">
                                            Automatically sync products, inventory, and orders between Shopify and ALANI360
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="h-100 shadow-sm">
                                    <Card.Body className="text-center">
                                        <div className="mb-3">
                                            <i className="fas fa-shopping-cart fa-3x text-success"></i>
                                        </div>
                                        <h5>Order Management</h5>
                                        <p className="text-muted">
                                            Centralized order processing and fulfillment across all your sales channels
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="h-100 shadow-sm">
                                    <Card.Body className="text-center">
                                        <div className="mb-3">
                                            <i className="fas fa-chart-line fa-3x text-success"></i>
                                        </div>
                                        <h5>Analytics & Insights</h5>
                                        <p className="text-muted">
                                            Comprehensive reporting and analytics to optimize your multi-channel strategy
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* CTA Section */}
                        <div className="text-center">
                            <Button variant="success" size="lg" className="me-3" href="/shopify/app">
                                Get Started
                            </Button>
                            <Button variant="outline-success" size="lg" href="/shopify/app">
                                Learn More
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ShopifyLanding;